import { AddComment } from "@/components/comments/AddComment.tsx";
import {
  ConfirmationModal,
  useModals,
  Box,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  LinkButtonTo,
  Skeleton,
  Stack,
} from "@skailer-com/uikit";
import { useNotificationHook } from "@/hooks/use-notification.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { CandidateCardProps } from "@/pages/recruit/candidates/components/CandidateCard.tsx";
import { ChangeStageModal } from "@/pages/recruit/candidates/widgets/ChangeStageModal.tsx";
import { ActionItem } from "@/pages/recruit/components/ActionItem.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useIntegrations } from "@/queries/integrations/use-integrations.query.ts";
import {
  useCandidateActionDelete,
  useCandidateActions,
} from "@/queries/recruit/use-candidate-actions.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAction, ApiActionTypes } from "@/types/action.types.ts";
import { ApiEntityTypes } from "@/types/common.types.ts";
import { ApiIntegrationType } from "@/types/integrations/integration.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function CandidateActions({ collapsed, candidate, applicantId }: CandidateCardProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { data: employee } = useEmployeeMe();
  const { canIEdit } = usePermissions();
  const modalsService = useModals();
  const { readActionsNotification } = useNotificationHook();
  const [editAction, setEditAction] = useState<ApiAction | undefined>(undefined);
  const { data: actions, isLoading: isActionsLoading } = useCandidateActions(
    candidate.id,
    !collapsed
  );
  const { data: integrations } = useIntegrations(canIEdit(ApiPermission.settings_integrations));

  const candidateActionDelete = useCandidateActionDelete();

  const navigate = useNavigate();

  const handleEditAction = useCallback(
    (action: ApiAction) => {
      if (action.type === ApiActionTypes.comment_added) {
        setEditAction(action);
        return;
      }
      if (action.type == ApiActionTypes.feedback) {
        if (action.feedback) {
          navigate(
            `/recruitment/candidates/${action.feedback.candidate_id}/feedbacks/${action.feedback.id}`
          );
        }
        return;
      }
      modalsService.openModal(ChangeStageModal, {
        pipelineId: candidate.pipeline.id,
        action: action,
        // candidate: candidate,
      });
    },
    [candidate]
  );

  const handleDeleteAction = useCallback(
    (actionId: number) => {
      modalsService.openModal(ConfirmationModal, {
        question: t("common:delete"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async function () {
          try {
            await candidateActionDelete.mutateAsync({
              applicantId: applicantId,
              candidateId: candidate.id,
              actionId: actionId,
            });
            analyticsService.trackEvent(analyticEvents.candidates.actionDeleted, {
              [analyticProperties.id]: candidate.id,
            });
          } catch {
            return;
          }
        },
      });
    },
    [candidate]
  );

  const canAddEventToGoogleCalendar = (action: ApiAction) => {
    if (employee == undefined) return false;
    if (action.author?.id != employee.id) return false;

    const googleCalendarIntegrationInactive = !(
      integrations &&
      integrations.some(
        (i) =>
          i.integration_type === ApiIntegrationType.google_calendar_service_account && i.is_active
      )
    );

    return [ApiActionTypes.event].indexOf(action.type) !== -1 && googleCalendarIntegrationInactive;
  };

  const googleCalendarURL = (action: ApiAction) => {
    if (!action.event) return "";

    const formatDate = (date: Date) =>
      date
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}Z$/, "Z");

    const start = formatDate(action.event.datetime_start);
    const end = formatDate(action.event.datetime_end);

    const url = new URL("https://calendar.google.com/calendar/render");
    url.searchParams.set("action", "TEMPLATE");
    url.searchParams.set("text", action.event.title);
    url.searchParams.set("dates", `${start}/${end}`);
    url.searchParams.set("details", action.event.description);

    if (action.event.attendees && action.event.attendees.length > 0) {
      const emails = action.event.attendees
        .filter((attendee) => attendee.email)
        .map((attendee) => attendee.email)
        .join(",");

      if (emails) {
        url.searchParams.set("add", emails);
      }
    }

    if (action.event.location) url.searchParams.set("location", action.event.location);

    return url.toString();
  };

  const canDeleteCandidateAction = (action: ApiAction) => {
    if (employee == undefined) return false;
    if (canIEdit(ApiPermission.delete_all_comments)) return true;
    if (action.author?.id != employee.id) return false;

    // Простые люди могут удалять события, после отмены события
    // if (action.event && action.event.is_cancelled) return true;

    return (
      [
        ApiActionTypes.stage_changed,
        ApiActionTypes.comment_added,
        ApiActionTypes.disqualified,
        ApiActionTypes.qualified,
      ].indexOf(action.type) !== -1
    );
  };

  const canEditCandidateAction = (action: ApiAction) => {
    return (
      employee != undefined &&
      action.author?.id === employee?.id &&
      [
        ApiActionTypes.stage_changed,
        ApiActionTypes.comment_added,
        ApiActionTypes.disqualified,
        ApiActionTypes.qualified,
        ApiActionTypes.feedback,
      ].indexOf(action.type) !== -1
    );
  };

  useEffect(() => {
    readActionsNotification(actions);
  }, [actions]);

  return (
    <>
      {!collapsed && (
        <Box className={"py-(--gap-lg)"}>
          <AddComment
            entityId={candidate.id}
            entityType={ApiEntityTypes.candidate}
            draftId={`candidate-${candidate.id}`}
          />
        </Box>
      )}
      {isActionsLoading && <Skeleton className="w-full h-10" />}
      {!collapsed && (
        <Stack gap={"lg"}>
          {actions &&
            actions.slice(0, showAll ? actions.length : 5).map((action) =>
              editAction && editAction.id === action.id ? (
                <AddComment
                  action={action}
                  key={`comment-v2-edit-${action.id}`}
                  entityId={action.item_id}
                  entityType={ApiEntityTypes.candidate}
                  isCollapsedOnInit={false}
                  onSaved={() => {
                    setEditAction(undefined);
                  }}
                />
              ) : (
                <ActionItem
                  action={action}
                  key={`comment-v2-${action.id}`}
                  actionsSlot={
                    (canEditCandidateAction(action) ||
                      canDeleteCandidateAction(action) ||
                      canAddEventToGoogleCalendar(action)) && (
                      <DropdownMenu
                        trigger={
                          <FontAwesomeIcon
                            icon={"fa-light fa-ellipsis"}
                            className={"cursor-pointer"}
                          />
                        }
                      >
                        {canEditCandidateAction(action) && (
                          <DropdownMenuItem
                            onClick={() => handleEditAction(action)}
                            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                            title={t("common:edit")}
                          />
                        )}
                        {canAddEventToGoogleCalendar(action) && (
                          <DropdownMenuItem
                            onClick={() => window.open(googleCalendarURL(action), "_blank")}
                            leftIcon={<FontAwesomeIcon icon={"fa-light fa-calendar-plus"} />}
                            title={t("calendar:add_event_to_google_calendar")}
                          />
                        )}
                        {canDeleteCandidateAction(action) && (
                          <DropdownMenuItem
                            onClick={() => handleDeleteAction(action.id)}
                            leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                            variant={"menuDanger"}
                            title={t("common:delete") + "..."}
                          />
                        )}
                      </DropdownMenu>
                    )
                  }
                />
              )
            )}
          {actions && actions.length > 5 && (
            <Box>
              <LinkButtonTo
                onClick={() => {
                  setShowAll(!showAll);
                }}
                size={"sm"}
                variant={"secondary"}
              >
                {showAll ? t("common:collapse") : t("common:show_more")}
              </LinkButtonTo>
            </Box>
          )}
        </Stack>
      )}
    </>
  );
}
